import { Children, forwardRef, type ReactElement, type ReactNode } from 'react';

import {
  type DataTestId,
  type MaskingProps,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';
import { _canReceiveDefaultTextFormat } from '@dynatrace/strato-components/core';
import { Flex, Divider } from '@dynatrace/strato-components/layouts';

import { Action, type TitleBarActionProps } from './Action.js';
import { Navigation, type TitleBarNavigationProps } from './Navigation.js';
import { Prefix, type TitleBarPrefixProps } from './Prefix.js';
import { Subtitle, type TitleBarSubtitleProps } from './Subtitle.js';
import { Suffix, type TitleBarSuffixProps } from './Suffix.js';
import { Title, type TitleBarTitleProps } from './Title.js';
import { titleSubtitleWrapperCSS } from './TitleBar.sty.js';
import { isSlot } from '../../core/utils/is-slot.js';
import { globalSprinkles } from '../../styles/safe-sprinkles.js';

//#region Typings
/**
 * Accepted properties for TitleBar
 * @public
 */
export interface TitleBarProps
  extends WithChildren,
    StylingProps,
    DataTestId,
    MaskingProps {
  /**
   * Whether a divider is displayed after the TitleBar component.
   * @defaultValue true
   */
  showDivider?: boolean;
}

type TitleBarSlots = {
  title?: ReactElement<TitleBarTitleProps>;
  subtitle?: ReactElement<TitleBarSubtitleProps>;
  navigation?: ReactElement<TitleBarNavigationProps>;
  prefix?: ReactElement<TitleBarPrefixProps>;
  suffix?: ReactElement<TitleBarSuffixProps>;
  action?: ReactElement<TitleBarActionProps>;
};
//#endregion

/** Iterates over child nodes to extract specific component slots. */
function getTitleBarSlots(children: ReactNode) {
  const slots: TitleBarSlots = {};

  Children.forEach(children, (child) => {
    if (isSlot(child, Title)) {
      slots.title = child;
    } else if (isSlot(child, Subtitle)) {
      slots.subtitle = child;
    } else if (isSlot(child, Navigation)) {
      slots.navigation = child;
    } else if (isSlot(child, Suffix)) {
      slots.suffix = child;
    } else if (isSlot(child, Prefix)) {
      slots.prefix = child;
    } else if (isSlot(child, Action)) {
      slots.action = child;
    }
  });

  return slots;
}

/**
 * The TitleBar component can be used for a consistent layout for section titles and provides
 * layout slots for interactive and navigational elements.
 */
const TitleBarRoot = /* @__PURE__ */ forwardRef<HTMLDivElement, TitleBarProps>(
  (props: TitleBarProps, forwardedRef) => {
    const {
      children,
      showDivider = true,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      'data-dtrum-mask': dataDtrumMask,
      'data-dtrum-allow': dataDtrumAllow,
      ...remainingProps
    }: TitleBarProps = props;
    const slots = getTitleBarSlots(children);

    const titleIsText =
      !!slots.title && _canReceiveDefaultTextFormat(slots.title.props.children);
    const subtitleIsText =
      !!slots.subtitle &&
      _canReceiveDefaultTextFormat(slots.subtitle?.props.children);
    const titleAndSubtitleIsText =
      (titleIsText && !slots.subtitle) || (titleIsText && subtitleIsText);

    return (
      <Flex
        ref={forwardedRef}
        {...remainingProps}
        width="100%"
        flexDirection="column"
        gap={0}
        className={consumerClassName}
        style={consumerStyle}
        data-testid={dataTestId}
        data-dtrum-mask={dataDtrumMask}
        data-dtrum-allow={dataDtrumAllow}
      >
        <Flex flexDirection="column" gap={8}>
          {slots.navigation && (
            <Flex width="100%" justifyContent="space-between" gap={8}>
              {slots.navigation}
              {slots.action}
            </Flex>
          )}
          <Flex width="100%" justifyContent="space-between" gap={8}>
            <Flex flexWrap="wrap" gap={8} width="100%" minWidth={0}>
              {slots.prefix}
              {(slots.title || slots.subtitle) && (
                <div
                  className={titleSubtitleWrapperCSS({
                    childrenAreText: titleAndSubtitleIsText,
                  })}
                >
                  {slots.title}
                  {slots.subtitle}
                </div>
              )}
              {slots.suffix}
            </Flex>
            {!slots.navigation && slots.action}
          </Flex>
        </Flex>
        {showDivider && (
          <Divider flexItem className={globalSprinkles({ marginTop: 8 })} />
        )}
      </Flex>
    );
  },
);

(TitleBarRoot as typeof TitleBarRoot & { displayName: string }).displayName =
  'TitleBar';

/**
 * The TitleBar component can be used for a consistent layout for section titles and provides
 * layout slots for interactive and navigational elements.
 * @public
 */
export const TitleBar = Object.assign(TitleBarRoot, {
  Title,
  Subtitle,
  Suffix,
  Navigation,
  Prefix,
  Action,
});
